import API_BASE_URL from '../config/api';

/**
 * Form Analytics Tracker
 * Tracks user interactions with forms for analytics purposes
 */

class FormAnalyticsTracker {
  constructor(formId, apiBaseUrl = API_BASE_URL, fingerprint = null, formVersionId = null) {
    this.formId = formId;
    this.formVersionId = formVersionId;
    this.apiBaseUrl = apiBaseUrl;
    this.sessionId = null;
    this.isTracking = true;
    this.isFormStarted = false;
    this.currentStepIndex = 0;
    this.hasStarted = false;
    this.startTime = null;
    this.currentStep = 0;
    this.stepStartTimes = {};
    this.trackerId = Math.random().toString(36).substr(2, 9); // Unique ID for debugging
    this.fingerprint = fingerprint || this.generateBasicFingerprint(); // Use provided fingerprint or fallback
    this.fingerprintReady = true; // Fingerprint is ready immediately
    
    console.log('Creating FormAnalyticsTracker for form:', formId);
    console.log('Form version ID:', formVersionId);
    console.log('Input apiBaseUrl:', apiBaseUrl);
    console.log('Final API URL:', this.apiBaseUrl);
    console.log('Will make requests to:', `${this.apiBaseUrl}/analytics/forms/${formId}/events`);
    console.log('Using fingerprint:', this.fingerprint ? this.fingerprint.substring(0, 16) + '...' : 'none');
    
    // Track page view now that fingerprint is ready
    this.handlePageLoad();
  }

  /**
   * Generate a basic fingerprint as fallback when enhanced fingerprint is not provided
   */
  generateBasicFingerprint() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      canvas: canvas.toDataURL(),
      timestamp: Date.now()
    };
    
    return btoa(JSON.stringify(fingerprint)).substring(0, 32);
  }

  /**
   * Setup event listeners for form interactions
   */
  setupEventListeners() {
    // Track when user leaves the page (potential form abandon)
    window.addEventListener('beforeunload', this.handleFormAbandon);
    
    // Track when form becomes visible (for embeds)
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.classList.contains('form-container')) {
            this.handlePageLoad();
          }
        });
      });
      
      // Observe form containers
      setTimeout(() => {
        const formContainers = document.querySelectorAll('.form-container, [data-form-id]');
        formContainers.forEach(container => observer.observe(container));
      }, 100);
    }
  }

  /**
   * Track a generic analytics event
   */
  async trackEvent(eventType, additionalData = {}) {
    if (!this.isTracking) {
      console.log('Analytics tracking is disabled for tracker:', this.trackerId);
      return;
    }
    
    console.log('Tracking event:', eventType, additionalData, 'trackerId:', this.trackerId);
    
    try {
      const eventData = {
        event_type: eventType,
        step_id: additionalData.stepId || null,
        step_index: additionalData.stepIndex,
        field_id: additionalData.fieldId || null,
        form_version_id: this.formVersionId || null, // Include form version for tracking changes
        fingerprint: this.fingerprint, // Include enhanced fingerprint
        event_data: {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          referrer: document.referrer,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          ...additionalData.eventData
        }
      };

      console.log('Sending analytics event:', eventData);

      const response = await fetch(`${this.apiBaseUrl}/analytics/forms/${this.formId}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        const result = await response.json();
        if (!this.sessionId) {
          this.sessionId = result.session_id;
        }
        console.log('Analytics event tracked successfully:', eventType, result);
      } else {
        console.warn('Failed to track analytics event:', eventType, response.status, await response.text());
      }
    } catch (error) {
      console.warn('Error tracking analytics event:', eventType, error);
    }
  }

  /**
   * Track page load/view
   */
  handlePageLoad() {
    this.trackEvent('page_view', {
      eventData: {
        user_agent: navigator.userAgent,
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    });
  }

  /**
   * Track form start (first interaction)
   */
  handleFormStart(stepId = null, stepIndex = 0, fieldId = null) {
    if (this.hasStarted) return;
    
    this.hasStarted = true;
    this.startTime = new Date();
    this.currentStep = stepIndex;
    
    this.trackEvent('form_start', {
      stepId,
      stepIndex,
      fieldId,
      eventData: {
        start_time: this.startTime.toISOString()
      }
    });
  }

  /**
   * Track step start
   */
  handleStepStart(stepId, stepIndex, stepTitle = null) {
    this.currentStep = stepIndex;
    this.stepStartTimes[stepIndex] = new Date();

    console.log(`StepIndex is ${stepIndex}`);

    this.trackEvent('step_start', {
      stepId,
      stepIndex,
      eventData: {
        step_title: stepTitle,
        step_start_time: this.stepStartTimes[stepIndex].toISOString()
      }
    });
  }

  /**
   * Track step completion
   */
  handleStepComplete(stepId, stepIndex, stepTitle = null) {
    const stepStartTime = this.stepStartTimes[stepIndex];
    const timeSpent = stepStartTime ? new Date() - stepStartTime : null;
    
    this.trackEvent('step_complete', {
      stepId,
      stepIndex,
      eventData: {
        step_title: stepTitle,
        time_spent_seconds: timeSpent ? Math.round(timeSpent / 1000) : null,
        completed_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track form submission
   */
  handleFormSubmit(r_id = null) {
    const totalTime = this.startTime ? new Date() - this.startTime : null;
    
    this.trackEvent('form_submit', {
      eventData: {
        total_time_seconds: totalTime ? Math.round(totalTime / 1000) : null,
        completed_at: new Date().toISOString(),
        success: true,
        response_id: r_id
      }
    });
    
  }

  /**
   * Track form abandonment
   */
  handleFormAbandon() {
    if (!this.hasStarted || !this.isTracking) return;
    
    const totalTime = this.startTime ? new Date() - this.startTime : null;
    
    this.trackEvent('form_abandon', {
      eventData: {
        abandon_step: this.currentStep,
        total_time_seconds: totalTime ? Math.round(totalTime / 1000) : null,
        abandoned_at: new Date().toISOString()
      }
    });
  }

  /**
   * Setup automatic tracking for form fields
   */
  setupFormTracking(formElement) {
    if (!formElement) {
      console.warn('Form element not found for analytics tracking');
      return;
    }

    let firstInteraction = true;

    // Track first interaction with any form field
    formElement.addEventListener('focusin', (e) => {
      if (firstInteraction && e.target.matches('input, textarea, select')) {
        firstInteraction = false;
        
        // Find step information
        const stepElement = e.target.closest('[data-step-id]');
        const stepId = stepElement?.dataset.stepId;
        const stepIndex = stepElement?.dataset.stepIndex ? parseInt(stepElement.dataset.stepIndex) : 0;
        const fieldId = e.target.id || e.target.name || e.target.dataset.fieldId;
        
        this.handleFormStart(stepId, stepIndex, fieldId);
      }
    });

    // Track step navigation
    const stepButtons = formElement.querySelectorAll('[data-step-action]');
    stepButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const action = button.dataset.stepAction;
        const stepElement = button.closest('[data-step-id]');
        const stepId = stepElement?.dataset.stepId;
        const stepIndex = stepElement?.dataset.stepIndex ? parseInt(stepElement.dataset.stepIndex) : 0;
        const stepTitle = stepElement?.dataset.stepTitle;

        if (action === 'next') {
          // For "next" button, only track step completion
          this.handleStepComplete(stepId, stepIndex, stepTitle);
          
          // Track the start of the next step
          setTimeout(() => {
            const nextStepIndex = stepIndex + 1;
            const nextStepElement = formElement.querySelector(`[data-step-index="${nextStepIndex}"]`);
            if (nextStepElement) {
              const nextStepId = nextStepElement.dataset.stepId;
              const nextStepTitle = nextStepElement.dataset.stepTitle;
              this.handleStepStart(nextStepId, nextStepIndex, nextStepTitle);
            }
          }, 100);
        } 
      });
    });

    // Remove the general form submit listener since we handle it in button clicks
    // formElement.addEventListener('submit', (e) => {
    //   this.handleFormSubmit();
    // });
  }

  /**
   * Manually trigger step start (useful for single-page forms or custom navigation)
   */
  triggerStepStart(stepId, stepIndex, stepTitle = null) {
    this.handleStepStart(stepId, stepIndex, stepTitle);
  }

  /**
   * Manually trigger step complete
   */
  triggerStepComplete(stepId, stepIndex, stepTitle = null) {
    this.handleStepComplete(stepId, stepIndex, stepTitle);
  }

  /**
   * Disable tracking (useful for testing or privacy settings)
   */
  disableTracking() {
    console.log('Analytics tracking disabled');
    this.isTracking = false;
  }

  /**
   * Enable tracking
   */
  enableTracking() {
    console.log('Analytics tracking enabled');
    this.isTracking = true;
  }

  /**
   * Destroy the tracker and clean up event listeners
   */
  destroy() {
    console.log('Destroying analytics tracker:', this.trackerId);
    this.isTracking = false;
    window.removeEventListener('beforeunload', this.handleFormAbandon);
  }
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FormAnalyticsTracker;
}

// Also make available globally for direct script inclusion
if (typeof window !== 'undefined') {
  window.FormAnalyticsTracker = FormAnalyticsTracker;
}

export default FormAnalyticsTracker;
